// src/features/auth/hooks/useAuth.ts
import { useEffect } from 'react';
import useAuthStore from '../../../store/authStore';
import authService from '../../../services/authService';
import { fetchUserProfile } from '../../account/api/accountApi';
import { shouldBypassAuth, DEV_MOCK_USER } from '../../../config/devMode';

/**
 * One profile request per page load, shared by every component that mounts
 * useAuth (header, hero, route guard…). Previously each of them fired its
 * own /account/profile call.
 */
let bootstrapPromise: Promise<void> | null = null;

async function bootstrapSession(): Promise<void> {
  const store = useAuthStore.getState();
  if (store.bootstrapped) return;
  if (!bootstrapPromise) {
    bootstrapPromise = (async () => {
      if (authService.isAuthenticated() && !useAuthStore.getState().user) {
        try {
          // Try to fetch user profile (sets the user on success)
          await fetchUserProfile();
        } catch (error) {
          // If error, clear tokens as they might be expired
          authService.logout();
        }
      }
      useAuthStore.getState().setBootstrapped(true);
    })();
  }
  return bootstrapPromise;
}

export const useAuth = () => {
  const { user, isLoading, error, bootstrapped, setUser } = useAuthStore();
  const devBypass = shouldBypassAuth();

  // Check authentication status on mount
  useEffect(() => {
    // In dev bypass mode, set the mock user if not already set
    if (devBypass) {
      if (!user) setUser(DEV_MOCK_USER);
      return;
    }
    if (!bootstrapped) bootstrapSession();
  }, [user, devBypass, setUser, bootstrapped]);

  // In dev bypass mode, always return authenticated with mock user
  if (devBypass) {
    return {
      isAuthenticated: true,
      user: user || DEV_MOCK_USER,
      isLoading: false,
      error: null,
      bootstrapped: true,
    };
  }

  return {
    isAuthenticated: !!user,
    user,
    isLoading,
    error,
    bootstrapped,
  };
};

export default useAuth;
